import React, { useMemo } from 'react';
import { _cs } from '@togglecorp/fujs';
import { IoChevronBack, IoChevronForward } from '../../node_modules/react-icons/io5/index.esm.js';
import { genericMemo } from '../../utils/index.js';
import SelectInput from '../SelectInput/index.js';
import Button from '../Button/index.js';
import styles from './styles.css.js';

var _IoChevronBack, _IoChevronForward;

function range(start, end) {
  const foo = [];

  for (let i = start; i <= end; i += 1) {
    foo.push(i);
  }

  return foo;
}

class Side {
  constructor(capacity, demand) {
    this.capacity = capacity;
    this.demand = demand;
    this.excess = this.capacity - this.demand;
  }

  hasShortage() {
    return this.excess < 0;
  }

  increaseCapacity(inc) {
    this.capacity += inc;
    this.excess += inc;
  }

  decreaseCapacity(dec) {
    this.capacity += dec;
    this.excess += dec;
  }

  optimizeCapacity() {
    if (this.excess > 0) {
      this.capacity -= this.excess;
      this.excess = 0;
    }
  }

}

function applyPagination(totalCapacity, active, total) {
  let showAllPages = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : false;

  if (showAllPages) {
    return range(1, total).map(index => {
      if (index === active) {
        return {
          type: 'fakeButton',
          label: String(active),
          key: 'active'
        };
      }

      return {
        type: 'button',
        index
      };
    });
  }

  const oneSideCapacity = (totalCapacity - 1) / 2;
  const startIndex = 1;
  const lastIndex = total; // Once upon a time, there were two sides of a town
  // And every year, each got equal amount of ration
  // But, they had a variable demand, and each year it could change

  const right = new Side(oneSideCapacity, active - startIndex);
  const left = new Side(oneSideCapacity, lastIndex - active); // So the two sides made a treaty
  // If any of the side had an excess that year and the other side had a shortage,
  // they had to give the excess to the other side
  // That way, all the ration would be used

  const leftExcess = left.excess;
  const rightExcess = right.excess;

  if (right.hasShortage() && leftExcess > 0) {
    right.increaseCapacity(leftExcess);
  } else if (left.hasShortage() && right.excess > 0) {
    left.increaseCapacity(rightExcess);
  }

  left.optimizeCapacity();
  right.optimizeCapacity();
  const lst = [];

  if (right.capacity > 0) {
    if (right.excess >= 0) {
      lst.push(...range(startIndex, active - 1).map(i => ({
        type: 'button',
        index: i
      })));
    } else {
      lst.push({
        type: 'button',
        index: startIndex
      }, {
        type: 'span',
        label: '…',
        key: 'startTick'
      }, ...range(active - (right.capacity - 2), active - 1).map(i => ({
        type: 'button',
        index: i
      })));
    }
  }

  lst.push({
    type: 'fakeButton',
    label: String(active),
    key: 'active'
  });

  if (left.capacity > 0) {
    if (left.excess >= 0) {
      lst.push(...range(active + 1, lastIndex).map(i => ({
        type: 'button',
        index: i
      })));
    } else {
      lst.push(...range(active + 1, active + (left.capacity - 2)).map(i => ({
        type: 'button',
        index: i
      })), {
        type: 'span',
        label: '...',
        key: 'endTick'
      }, {
        type: 'button',
        index: lastIndex
      });
    }
  }

  return lst;
}

const defaultItemPerPageOptions = [{
  label: '10 / page',
  key: 10
}, {
  label: '25 / page',
  key: 25
}, {
  label: '50 / page',
  key: 50
}];

const itemsPerPageKeySelector = o => o.key;

const itemsPerPageLabelSelector = o => o.label;

function Pager(props) {
  const {
    activePage: activePageProps,
    className,
    itemsCount,
    onActivePageChange,
    options,
    maxItemsPerPage = 25,
    totalCapacity = 7,
    disabled = false,
    infoVisibility = 'visible',
    pagesNextPrevControlLabelShown = false,
    pageNextPrevControlHidden = false,
    pagesControlHidden = false,
    pagesControlLabelHidden = false,
    allPagesExpanded = false
  } = props;
  const showingTitle = 'Showing';
  const ofTitle = 'of';
  const rangeIndicator = '-'; // NOTE: activePage can never be 0

  const activePage = Math.max(activePageProps, 1); // NOTE: number of pages can never be 0

  const numPages = Math.max(Math.ceil(itemsCount / maxItemsPerPage), 1);
  const offset = (activePage - 1) * maxItemsPerPage;
  const itemsOnPage = Math.min(maxItemsPerPage, itemsCount - offset);
  const currentItemsStart = itemsOnPage > 0 ? offset + 1 : offset;
  const currentItemsEnd = offset + itemsOnPage;
  const pages = applyPagination(totalCapacity, activePage, numPages, allPagesExpanded);
  const pageList = pages.length > 1 && /*#__PURE__*/React.createElement("div", {
    className: _cs(styles.pageList, pagesControlLabelHidden && styles.pageNumberHidden)
  }, !pageNextPrevControlHidden && /*#__PURE__*/React.createElement(Button, {
    name: activePage - 1,
    className: styles.pageButton,
    onClick: onActivePageChange,
    disabled: activePage <= 1 || disabled,
    icons: _IoChevronBack || (_IoChevronBack = /*#__PURE__*/React.createElement(IoChevronBack, null)),
    variant: "action"
  }, pagesNextPrevControlLabelShown && 'Previous'), !pagesControlHidden && pages.map(page => {
    if (page.type === 'button') {
      return /*#__PURE__*/React.createElement(Button, {
        key: `button-${page.index}`,
        name: page.index,
        onClick: onActivePageChange,
        className: _cs(styles.pageButton, styles.pageNumberButton),
        disabled: disabled,
        variant: pagesControlLabelHidden ? 'secondary' : 'action'
      }, !pagesControlLabelHidden && page.index);
    }

    if (page.type === 'fakeButton') {
      return /*#__PURE__*/React.createElement(Button, {
        key: `button-${page.key}`,
        variant: pagesControlLabelHidden ? 'primary' : 'action',
        name: undefined,
        className: _cs(styles.pageButton, styles.pageNumberButton, styles.active)
      }, !pagesControlLabelHidden && page.label);
    }

    return /*#__PURE__*/React.createElement("div", {
      key: `span-${page.key}`,
      className: styles.pageSpan
    }, page.label);
  }), !pageNextPrevControlHidden && /*#__PURE__*/React.createElement(Button, {
    name: activePage + 1,
    onClick: onActivePageChange,
    disabled: activePage >= numPages || disabled,
    className: styles.pageButton,
    actions: _IoChevronForward || (_IoChevronForward = /*#__PURE__*/React.createElement(IoChevronForward, null)),
    variant: "action"
  }, pagesNextPrevControlLabelShown && 'Next'));
  const hasMultiplePages = itemsCount > maxItemsPerPage;
  const showInfo = useMemo(() => {
    if (infoVisibility === 'visible') {
      return true;
    }

    if (infoVisibility === 'hidden') {
      return false;
    }

    return hasMultiplePages;
  }, [infoVisibility, hasMultiplePages]);
  const info = showInfo && /*#__PURE__*/React.createElement("div", {
    className: styles.currentRangeInformation
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.showing
  }, showingTitle), /*#__PURE__*/React.createElement("div", {
    className: styles.range
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.from
  }, currentItemsStart), /*#__PURE__*/React.createElement("div", {
    className: styles.separator
  }, rangeIndicator), /*#__PURE__*/React.createElement("div", {
    className: styles.to
  }, currentItemsEnd)), /*#__PURE__*/React.createElement("div", {
    className: styles.of
  }, ofTitle), /*#__PURE__*/React.createElement("div", {
    className: styles.total
  }, itemsCount));
  const itemsPerPageOptions = options ?? defaultItemPerPageOptions;
  const minOption = useMemo(() => Math.min(0, ...itemsPerPageOptions.map(opt => opt.key)), [itemsPerPageOptions]); // eslint-disable-next-line react/destructuring-assignment

  const itemPerPageControl = !props.itemsPerPageControlHidden && itemsCount > minOption && /*#__PURE__*/React.createElement("div", {
    className: styles.itemsPerPage
  }, /*#__PURE__*/React.createElement(SelectInput, {
    name: "itemsPerPageSelection",
    className: styles.input,
    options: itemsPerPageOptions,
    keySelector: itemsPerPageKeySelector,
    labelSelector: itemsPerPageLabelSelector,
    value: maxItemsPerPage // eslint-disable-next-line react/destructuring-assignment
    ,
    onChange: props.onItemsPerPageChange,
    disabled: disabled,
    placeholder: "",
    optionsPopupClassName: styles.perPageOptionPopup,
    nonClearable: true,
    variant: "general"
  }));

  if (!pageList && !itemPerPageControl && !info) {
    return null;
  }

  return /*#__PURE__*/React.createElement("div", {
    className: _cs(className, styles.pager)
  }, pageList, (info || itemPerPageControl) && /*#__PURE__*/React.createElement("div", {
    className: styles.infoAndConfig
  }, info, itemPerPageControl));
}

var index = genericMemo(Pager);

export { index as default };
//# sourceMappingURL=index.js.map
